import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Form, Icon, Input } from "semantic-ui-react";

import { CUSTOM_CONFIRM_TYPES, CustomConfirm, useCan } from "components";
import { usePermanentRealTimeUser } from "hooks";
import { createFormHelper } from "lib/Form";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { ExactAccountModal, ExactSearch } from "modules/DealerDetails/components/Invoicing/components";
import {
  useDeleteFailedInvoiceMutation,
  useLinkExactAccountMutation,
  useRetrieveExactAccountMutation,
  useRetryExactInvoiceMutation
} from "modules/DealerDetails/components/Invoicing/hooks";
import { isAdmin } from "utils/common";

import type { Dealer, DealerLocation } from "models";
import type { ExactAccount } from "modules/DealerDetails/components/Invoicing/hooks";
import type { LocationAdminFormPayload } from "modules/DealerLocationDetails";

const FormHelper = createFormHelper<LocationAdminFormPayload>();

type InvoicingSettingsProps = {
  location: DealerLocation;
  dealer?: Dealer;
};

export const InvoicingSettings = ({ location, dealer }: InvoicingSettingsProps) => {
  const { t } = useTranslation();
  const { watch } = FormHelper.useFormContext();

  const [account, setAccount] = useState<ExactAccount | null>(null);
  const [exactAccountId, setExactAccountId] = useState("");
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
  const [isRetryConfirmOpen, setIsRetryConfirmOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const { user } = usePermanentRealTimeUser();
  const { mutate: linkExactAccount, isPending: isLinkExactAccountPending } = useLinkExactAccountMutation();
  const { mutate: retryExactInvoice, isPending: isRetryExactInvoicePending } = useRetryExactInvoiceMutation();
  const { mutate: deleteFailedInvoice, isPending: isDeleteFailedInvoicePending } = useDeleteFailedInvoiceMutation();
  const { mutate: retrieveExactAccount, isPending: isRetrieveExactAccountPending } = useRetrieveExactAccountMutation();

  const canRetryFailedInvoice = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.ExactInvoice);
  const canDeleteFailedInvoice = useCan(ACL_ACTIVITIES.Delete, ACL_RESOURCES.ExactInvoice);

  const isExactInvoicingEnabled = watch("is_exact_invoicing_enabled");
  const areFailedInvoiceActionsDisabled = !location.has_failed_invoices || isRetryExactInvoicePending || isDeleteFailedInvoicePending;

  const handleLinkAccount = (accountId: string) =>
    linkExactAccount({ dealer_location_id: location.id, account_id: accountId }, { onSuccess: () => setExactAccountId("") });

  const handleOpenAccountModal = () => {
    if (!location.exact_account_id) {
      setIsAccountModalOpen(true);
      return;
    }

    retrieveExactAccount(
      { dealer_location_id: location.id },
      {
        onSuccess: retrievedAccount => {
          setAccount(retrievedAccount);
          setIsAccountModalOpen(true);
        }
      }
    );
  };

  const handleCloseAccountModal = () => {
    setIsAccountModalOpen(false);
    setAccount(null);
  };

  if (!user || !isAdmin(user.role_id)) return null;

  return (
    <>
      <div className="dealer-location-subheading">{t("v8_invoicing_settings", "Invoicing settings")}</div>
      <Form.Field>
        <FormHelper.Field.Checkbox name="is_exact_invoicing_enabled" label={t("v8_enable_location_level_invoicing", "Enable location level invoicing")} />
      </Form.Field>

      {isExactInvoicingEnabled && (
        <>
          <Form.Field required>
            <label>{t("v8_exact_account_id", "Exact Account ID")}</label>
            <Input
              value={location.exact_account_id ?? exactAccountId}
              readOnly={!!location.exact_account_id}
              placeholder={t("v8_exact_account_id", "Exact Account ID")}
              onChange={(_, { value }) => setExactAccountId(value)}
              action={
                <>
                  {!!dealer && <ExactSearch dealerId={dealer.id} onSelectAccount={handleLinkAccount} />}
                  <Button
                    type="button"
                    color="green"
                    loading={isLinkExactAccountPending}
                    disabled={!!location.exact_account_id || isLinkExactAccountPending || !exactAccountId}
                    onClick={() => exactAccountId && handleLinkAccount(exactAccountId)}
                  >
                    {t("v8_link_account", "Link account")}
                  </Button>
                </>
              }
            />
          </Form.Field>
          <div className="dealer-location-button-row">
            <Button type="button" color="green" loading={isRetrieveExactAccountPending} disabled={isRetrieveExactAccountPending} onClick={handleOpenAccountModal}>
              {location.exact_account_id ? t("v8_update_exact_account", "Update Exact account") : t("v8_create_exact_account", "Create Exact account")}
            </Button>
          </div>

          {(canRetryFailedInvoice || canDeleteFailedInvoice) && (
            <>
              <div className="dealer-location-subheading">{t("v8_failed_invoices", "Failed invoices")}</div>
              <div className="dealer-location-button-row">
                {canRetryFailedInvoice && (
                  <Button type="button" color="orange" disabled={areFailedInvoiceActionsDisabled} onClick={() => setIsRetryConfirmOpen(true)}>
                    {t("v8_retry_sending_invoice", "Retry sending invoice")}
                    <Icon className="rotate right" />
                  </Button>
                )}
                {canDeleteFailedInvoice && (
                  <Button type="button" color="red" disabled={areFailedInvoiceActionsDisabled} onClick={() => setIsDeleteConfirmOpen(true)}>
                    {t("v8_delete_failed_invoice", "Delete failed invoice")}
                    <Icon className="trash" />
                  </Button>
                )}
              </div>
            </>
          )}
        </>
      )}

      <ExactAccountModal isOpen={isAccountModalOpen} account={account} locationId={location.id} onClose={handleCloseAccountModal} onSaved={() => setExactAccountId("")} />
      <CustomConfirm
        type={CUSTOM_CONFIRM_TYPES.Warning}
        isOpen={isRetryConfirmOpen}
        isLoading={isRetryExactInvoicePending}
        confirmMsg={t("v8_confirm_retry_failed_invoice", "Are you sure that you want to retry this invoice?")}
        handleCancel={() => setIsRetryConfirmOpen(false)}
        handleConfirm={() => retryExactInvoice({ dealer_location_id: location.id }, { onSuccess: () => setIsRetryConfirmOpen(false) })}
      />
      <CustomConfirm
        type={CUSTOM_CONFIRM_TYPES.Danger}
        isOpen={isDeleteConfirmOpen}
        isLoading={isDeleteFailedInvoicePending}
        confirmMsg={t("v8_confirm_delete_failed_invoice", "Are you sure that you want to delete this invoice?")}
        handleCancel={() => setIsDeleteConfirmOpen(false)}
        handleConfirm={() => deleteFailedInvoice({ dealer_location_id: location.id }, { onSuccess: () => setIsDeleteConfirmOpen(false) })}
      />
    </>
  );
};
